import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import moment from 'moment';
import Textarea from 'react-textarea-autosize';
import Parse from 'html-react-parser';
import Flatpickr from 'react-flatpickr';
import * as linkify from 'linkifyjs';
import {
  FormFeedback,
  Input,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { fetchFileEditions, updateFile } from '../../actions/Files';

import LinkifyExt from '../LinkifyExt';

import { nl2br } from '../../helpers';
import {
  getShare,
  userCan,
} from '../../helpers/Auth';
import {
  getDateFormatted,
  getRawDate,
  getUserTimeZone,
} from '../../helpers/DateTime';
import {
  eventSubscribe,
  eventUnsubscribe,
} from '../../helpers/Events';
import { userHasFeature } from '../../helpers/Features';
import { getSetting } from '../../helpers/Settings';

import useOutsideClick from '../../hooks/useOutsideClick';

import UpgradePlanButton from '../UpgradePlanButton';

const FileEditableField = ({
  dispatch,
  file,
  field,
  fieldType,
  fieldName,
  fieldPrefix,
  fieldValue,
  fileModalOpen,
  required,
  readOnly,
}) => {
  const intl = useIntl();

  const value = fieldValue || file[field];
  const placeholder = intl.formatMessage({
    defaultMessage: 'Click to add {fieldName}',
  }, {
    fieldName: fieldName || field,
  });
  const elRef = useRef();
  let fieldRef = useRef();
  const [editMode, setEditMode] = useState(false);
  const [editedValue, setEditedValue] = useState(value || '');
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [validationError, setValidationError] = useState('');

  const editDisabled = !userCan('file.edit', file) || getShare();
  const editOnClickDisabled = fieldType === 'url' && editedValue;
  const isFieldInvalid = editMode && !!(error || validationError);
  const expirationDateRequiredError = field === 'expires_at' && editedValue === null && getSetting('expiration_date') && getSetting('expiration_date_required');

  useEffect(() => {
    setEditedValue(value);
  }, [value]);

  const openEdit = (e) => {
    if (e) {
      e.preventDefault();
    }

    if (field === 'expires_at' && !userHasFeature('expiration-date')) return;

    if (!saving && !editDisabled && !readOnly) {
      setEditMode(true);
    }
  };

  useEffect(() => {
    if (editMode) {
      if (fieldRef.current) {
        fieldRef.current.focus();
      }
    }
  }, [editMode]);

  const saveChanges = useCallback((e, switching = false) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (required && !editedValue) {
      if (!switching) {
        setError(true);
      } else {
        setEditMode(false);
      }
      return;
    }

    if (expirationDateRequiredError) {
      if (!switching) {
        setEditMode(false);
      }
      return;
    }

    if (fieldType === 'url' && editedValue && !linkify.test(editedValue)) {
      if (!switching) {
        setValidationError(intl.formatMessage({ defaultMessage: 'The URL looks invalid' }));
      } else {
        setEditMode(false);
      }
      return;
    }

    if (!touched) {
      setEditMode(false);
      return;
    }

    setError(false);
    setValidationError('');
    setEditMode(false);
    setSaving(true);
    const editedFile = { ...file };
    if (field.indexOf('cf-') === 0) {
      const cId = parseInt(field.replace('cf-', ''));
      const cfIdx = file.custom_fields.findIndex(c => (c.id === cId));
      if (cfIdx >= 0) {
        editedFile.custom_fields[cfIdx].value = editedValue;
      } else {
        editedFile.custom_fields.push({ id: cId, value: editedValue });
      }
    } else {
      editedFile[field] = editedValue;
    }
    editedFile.inline_edit = true;
    dispatch(updateFile(editedFile)).then((response) => {
      setSaving(false);
      setTouched(false);
      if (response.parent_uuid && fileModalOpen) {
        dispatch(fetchFileEditions(editedFile.uuid));
      }
    });
  }, [
    dispatch,
    editedValue,
    expirationDateRequiredError,
    field,
    fieldType,
    file,
    fileModalOpen,
    intl,
    required,
    touched,
  ]);

  useEffect(() => {
    const onFileSwitch = () => {
      if (editMode) {
        saveChanges(null, true);
      }
    };

    eventSubscribe('fileSwitch', onFileSwitch);
    return () => {
      eventUnsubscribe('fileSwitch', onFileSwitch);
    };
  }, [saveChanges, editMode]);

  const cancelChanges = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    setEditedValue(value);
    setEditMode(false);
  };

  const handleFileChange = (e) => {
    setTouched(e.target.value !== value);
    setEditedValue(e.target.value);
    setValidationError('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && fieldType !== 'textarea') {
      saveChanges();
    }

    if (e.key === 'Escape') {
      cancelChanges();
    }
  };

  const displayFieldValue = () => {
    if (fieldType === 'textarea') {
      return Parse(nl2br(editedValue || placeholder));
    }

    if (fieldType === 'date') {
      return editedValue ? Parse(`${fieldPrefix}${getDateFormatted(editedValue)}`) : placeholder;
    }

    if (fieldType === 'url') {
      return editedValue ? (<LinkifyExt>{editedValue}</LinkifyExt>) : placeholder;
    }

    return (`${fieldPrefix}${editedValue || ''}` || placeholder);
  };

  useOutsideClick(elRef, (e) => {
    const { target } = e;

    const classesToExclude = [
      'fas',
      'flatpickr-monthDropdown-months',
      'flatpickr-prev-month',
      'flatpickr-next-month',
      'cur-year',
      'arrowUp',
      'arrowDown',
    ];
    const targetClasses = [...target.classList];

    if (
      editMode &&
      !targetClasses.some(r => classesToExclude.includes(r)) &&
      target.tagName !== 'path' &&
      target.tagName !== 'svg'
    ) {
      saveChanges();
    }
  });

  if (!editedValue && (!userCan('file.edit', file) || getShare())) {
    return null;
  }

  return (
    <>
      <div className={classnames('file-details-row', { editing: editMode })}>
        <span
          className={classnames(
            'editable-field',
            `editable-field-${fieldType}`,
            {
              'editable-field-editing': editMode,
              'editable-field-disabled':
                editDisabled ||
                editOnClickDisabled ||
                (field === 'expires_at' && !userHasFeature('expiration-date')),
              'editable-field-empty': !editedValue,
              'editable-field-read-only': readOnly,
            },
          )}
          role="button"
          tabIndex={0}
          title={(editMode || editDisabled || editOnClickDisabled) ? '' :
            intl.formatMessage({ defaultMessage: 'Click to edit' })}
          ref={elRef}
          onKeyUp={editOnClickDisabled ? () => {} : openEdit}
          onClick={editOnClickDisabled ? () => {} : openEdit}
        >
          {editMode ? (
            <>
              {(fieldType === 'text' || fieldType === 'url') && (
                <Input
                  type="text"
                  innerRef={fieldRef}
                  name={field}
                  defaultValue={editedValue}
                  disabled={saving}
                  onChange={handleFileChange}
                  onKeyDown={handleKeyDown}
                  invalid={isFieldInvalid}
                />
              )}
              {fieldType === 'textarea' && (
                <Textarea
                  ref={(ref) => { fieldRef = ref; }}
                  name={field}
                  className="form-control"
                  defaultValue={editedValue}
                  disabled={saving}
                  onChange={handleFileChange}
                  onKeyDown={handleKeyDown}
                  autoFocus
                />
              )}
              {fieldType === 'date' && (
                <div className="date-picker-container">
                  <Flatpickr
                    render={
                      ({ defaultValue, value: fpValue, ...props }, ref) => (
                        <Input {...props} defaultValue={defaultValue} innerRef={ref} />
                      )
                    }
                    value={editedValue}
                    options={{
                      allowInput: true,
                      now: getRawDate(moment().tz(getUserTimeZone())),
                    }}
                    onChange={(dates, date) => {
                      const now = getRawDate(moment(`${date} ${moment().format('HH:mm:ss')}`).tz(getUserTimeZone()));
                      const dateTime = now.utc().format('YYYY-MM-DD HH:mm:ss');
                      handleFileChange({ target: { name: field, value: dateTime } });
                    }}
                  />
                  <a
                    href="#clear"
                    className="input-clear"
                    title={intl.formatMessage({ defaultMessage: 'Clear' })}
                    onClick={(e) => {
                      e.preventDefault();
                      handleFileChange({ target: { name: field, value: null } });
                    }}
                  >
                    <i className="fas fa-times" />
                  </a>
                </div>
              )}
              <a
                href="#save"
                title={intl.formatMessage({ defaultMessage: 'Save' })}
                className={classnames({ disabled: expirationDateRequiredError || isFieldInvalid })}
                onClick={saveChanges}
              >
                <i className="fas fa-check" />
              </a>
              <a
                href="#cancel"
                title={intl.formatMessage({ defaultMessage: 'Cancel' })}
                onClick={cancelChanges}
              >
                <i className="fas fa-xmark" />
              </a>
            </>
          ) : (
            <>
              {displayFieldValue()}
              {(!editDisabled && editOnClickDisabled) && (
                <a
                  href="#edit"
                  className="edit-link"
                  onClick={openEdit}
                  title={intl.formatMessage({ defaultMessage: 'Edit' })}
                >
                  <i className="fas fa-pencil" />
                </a>
              )}
            </>
          )}
        </span>
        {field === 'expires_at' && (
          <UpgradePlanButton
            feature="expiration-date"
          />
        )}
      </div>
      {isFieldInvalid && (
        <FormFeedback style={{ display: 'block' }}>
          {validationError || intl.formatMessage({ defaultMessage: 'This field is required' })}
        </FormFeedback>
      )}
      {expirationDateRequiredError && (
        <FormFeedback style={{ display: 'block' }}>
          {validationError || intl.formatMessage({ defaultMessage: 'Expiration date is required in this workspace' })}
        </FormFeedback>
      )}
    </>
  );
};

const mapStateToProps = state => ({
  fileModalOpen: state.Files.fileModalOpen,
});

FileEditableField.propTypes = {
  dispatch: PropTypes.func.isRequired,
  file: PropTypes.object.isRequired,
  field: PropTypes.string.isRequired,
  fieldType: PropTypes.string,
  fieldName: PropTypes.string,
  fieldPrefix: PropTypes.string,
  fieldValue: PropTypes.any,
  fileModalOpen: PropTypes.bool.isRequired,
  required: PropTypes.bool,
  readOnly: PropTypes.bool,
};

FileEditableField.defaultProps = {
  fieldType: 'text',
  fieldName: '',
  fieldPrefix: '',
  fieldValue: null,
  required: false,
  readOnly: false,
};

export default connect(mapStateToProps)(FileEditableField);
